import type { IdZonaOperacaoPorta } from './oficinaCorte'

export type LadoVeiculo = 'esquerdo' | 'direito' | 'centro' | 'indefinido'

/**
 * Contrato canônico de inspeção e acerto espacial (raycast) sobre o veículo didático.
 * Desacopla a malha Three.js do modelo semântico de domínio.
 */
export interface HitVeiculo {
  /** Identificador único do veículo na cena (ex: 'veiculoPrincipal', 'veiculoAlvo') */
  veiculoId: string
  /** Identificador didático/semântico da peça (ex: 'capo', 'paraChoqueDianteiro', 'portaDianteiraEsquerda') */
  pecaId: string
  /** Lado da peça ou do impacto no referencial do veículo */
  lado: LadoVeiculo
  /** Identificador ou nome do mesh específico atingido */
  meshId?: string
  /** Ponto de impacto no referencial local do veículo [x, y, z] */
  pontoLocal: [number, number, number]
  /** Ponto de impacto no referencial de mundo [x, y, z] */
  pontoMundo: [number, number, number]
  /** Vetor normal de superfície no referencial de mundo (se disponível) */
  normalMundo?: [number, number, number]
  /** Distância linear da origem do raio até o ponto de impacto */
  distancia: number
  /** Índice da face triangular atingida (se disponível) */
  faceIndex?: number
}

/**
 * Deduz o lado do veículo a partir do nome semântico da peça ou coordenada X local.
 */
export function identificarLadoPeca(pecaId: string, xLocal?: number): LadoVeiculo {
  const normalizado = pecaId.toLowerCase()
  if (normalizado.includes('esquerda') || normalizado.includes('esq')) {
    return 'esquerdo'
  }
  if (normalizado.includes('direita') || normalizado.includes('dir')) {
    return 'direito'
  }

  if (xLocal !== undefined && Number.isFinite(xLocal)) {
    if (xLocal < -0.05) return 'esquerdo'
    if (xLocal > 0.05) return 'direito'
    return 'centro'
  }

  return 'indefinido'
}

/**
 * Mapeia um HitVeiculo para a zona semântica de porta mais próxima na Oficina,
 * respeitando os vínculos mecânicos (dobradiças, fechadura, corte de alívio).
 */
export function associarHitAZonaPorta(
  hit: HitVeiculo,
  portaDianteira = true,
): IdZonaOperacaoPorta | null {
  if (!hit.pecaId.includes('porta')) {
    return null
  }

  const [x, y, z] = hit.pontoLocal
  // No referencial local da porta/veículo:
  // Dobradiças estão próximas à coluna A (+Z na dianteira)
  // Trava/fechadura está próxima à coluna B (-Z na dianteira)
  // Esquadria da janela está na metade superior
  if (y > 0.95) {
    return 'esquadria'
  }

  if (portaDianteira) {
    // Dobradiça dianteira alta/baixa
    if (z > 0.4) {
      return y > 0.6 ? 'dobradicaSuperior' : 'dobradicaInferior'
    }
    // Trava traseira da porta dianteira (junto à coluna B)
    if (z < -0.3) {
      return 'trava'
    }
    // Chapa central
    return 'chapa'
  }

  return null
}
